// BrushPanelItemButton — ブラシパネル item (canvas サムネ + 重ねラベル) の共有 JSX。
//
// `brushPanelShared.ts` は class 定数だけを持つ純粋 `.ts` (JSX 不可) なので、item の
// 実 DOM 構造 (= 2026-08-19 に「ラベルをサンプルに重ねる」形へ作り直した部分) は
// この `.tsx` に切り出す。デスクトップ タブ版 (`ModernBrushPanel.tsx`) / フォン版
// (`ModernBrushPanelFlat.tsx`) の両方が同じ item 構造を必要とする (`brushPanelShared.ts`
// 冒頭コメント「両バリアントは item の見た目を共有できる」) ので、ここで 1 箇所に
// まとめて 2 箇所に同じ JSX を書かない。
//
// 挙動 (aria-pressed / aria-label / onClick) は旧実装から不変。変わったのは DOM 構造
// (横並び → 重ね) だけ (詳細な理由は `brushPanelShared.ts` の該当コメント参照)。

import {
  type BrushPanelItem,
  ITEM_CANVAS_CLASS,
  ITEM_LABEL_CLASS,
  ITEM_LABEL_TEXT_SHADOW,
  itemButtonClass,
  manualIdForBrushPanelItem,
  THUMB_H,
  THUMB_W,
} from './brushPanelShared.js';

export interface BrushPanelItemButtonProps {
  readonly item: BrushPanelItem;
  /** この item が現在選択中か (= ハイライト表示するか)。 */
  readonly active: boolean;
  /** item クリック時に呼ぶ (= 呼び出し側が onSelectPen/onSelectTexturePreset を配線)。 */
  readonly onSelect: (item: BrushPanelItem) => void;
  /** サムネ描画 (`putImageData`) のために呼び出し側が canvas 実体を回収する。 */
  readonly canvasRef: (el: HTMLCanvasElement | null) => void;
}

export const BrushPanelItemButton = ({
  item,
  active,
  onSelect,
  canvasRef,
}: BrushPanelItemButtonProps): React.ReactNode => (
  <button
    type="button"
    aria-pressed={active}
    aria-label={`ブラシ ${item.label}`}
    onClick={() => onSelect(item)}
    data-manual={manualIdForBrushPanelItem(item)}
    className={itemButtonClass(active)}
  >
    <canvas
      ref={canvasRef}
      width={THUMB_W}
      height={THUMB_H}
      // 装飾的 (= 親 <button> の aria-label が名称を読み上げる)。role も
      // aria-hidden も付けない (canvas は focusable 扱いで aria-hidden と
      // 衝突する、biome a11y。TexturePresetPicker の TexturePresetThumb と
      // 同じ流儀)。
      className={ITEM_CANVAS_CLASS}
    />
    <span className={ITEM_LABEL_CLASS} style={{ textShadow: ITEM_LABEL_TEXT_SHADOW }}>
      {item.label}
    </span>
  </button>
);
